import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Plus, Play, Square, Trash2, Users, Crown, X } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, timeAgo } from "./engShared";
import { appConfirm } from "@/components/AppDialogHost";

const CST_STATUS = {
  draft: ["Draft", "text-ink-muted border-line bg-bg-elevated"],
  active: ["Live", "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"],
  closed: ["Closed", "text-ink-muted border-line bg-bg-elevated"],
};

export default function EngContests() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [f, setF] = useState({});
  const [entriesFor, setEntriesFor] = useState(null);
  const [entries, setEntries] = useState([]);

  const load = () => api.get("/engagement/contests").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      const r = await api.post(`/engagement/contests/${id}/action`, { action });
      if (action === "pick_winner" && r.data.winner) {
        toast.success(`🏆 Winner: ${r.data.winner.name} (${r.data.winner.email})`, { duration: 8000 });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/engagement/contests", f);
      toast.success("Contest created — activate it to open entries");
      setF({}); setShowNew(false); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Create failed"); }
  };
  const openEntries = async (c) => {
    setEntriesFor(c);
    const r = await api.get(`/engagement/contests/${c.contest_id}/entries`);
    setEntries(r.data.items || []);
  };

  return (
    <div className="space-y-4" data-testid="eng-contests-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Contests</h1>
          <p className="text-sm text-ink-secondary">Run giveaways — entries collect on your public listener page, winners drawn fairly at random.</p>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="eng-contest-new-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-xs font-bold">
          <Plus className="h-3.5 w-3.5" /> New Contest
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-contest-form">
          <input required value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Contest title" data-testid="eng-contest-title"
            className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" maxLength={200} />
          <div className="grid grid-cols-2 gap-3">
            <input value={f.prize || ""} onChange={(e) => setF({ ...f, prize: e.target.value })} placeholder="Prize (e.g. 2 concert tickets)" data-testid="eng-contest-prize"
              className="rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" maxLength={300} />
            <input value={f.question || ""} onChange={(e) => setF({ ...f, question: e.target.value })} placeholder="Entry question (optional)" data-testid="eng-contest-question"
              className="rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" maxLength={300} />
          </div>
          <textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="Description / rules" data-testid="eng-contest-desc"
            className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" maxLength={2000} />
          <button type="submit" data-testid="eng-contest-create-btn" className="rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-bold">Create contest</button>
        </form>
      )}

      <div className="space-y-3">
        {!items.length && <div className="rounded-xl border border-line bg-bg-surface p-8 text-center text-sm text-ink-muted" data-testid="eng-contests-empty">No contests yet.</div>}
        {items.map((c) => (
          <div key={c.contest_id} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`eng-contest-${c.contest_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Trophy className="h-4 w-4 text-brand-amber" />
              <span className="text-sm font-semibold flex-1">{c.title}</span>
              <Badge meta={CST_STATUS[c.status]} />
              <span className="text-[11px] font-mono text-ink-muted">{c.entry_count || 0} entries</span>
            </div>
            {c.prize && <div className="text-xs text-brand-amber mt-1">🏆 {c.prize}</div>}
            {c.description && <div className="text-xs text-ink-secondary mt-1">{c.description}</div>}
            {c.winner_name && <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand-amber/50 bg-brand-amber/10 text-brand-amber px-3 py-1 text-xs font-bold" data-testid={`eng-contest-winner-${c.contest_id}`}><Crown className="h-3.5 w-3.5" /> Winner: {c.winner_name}</div>}
            <div className="flex gap-1.5 mt-3">
              {c.status !== "active" && !c.winner_name && <button onClick={() => act(c.contest_id, "activate")} data-testid={`eng-contest-activate-${c.contest_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-300 px-3 py-1 text-[11px]"><Play className="h-3 w-3" /> Open entries</button>}
              {c.status === "active" && <button onClick={() => act(c.contest_id, "close")} data-testid={`eng-contest-close-${c.contest_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 text-[11px]"><Square className="h-3 w-3" /> Close</button>}
              <button onClick={() => openEntries(c)} data-testid={`eng-contest-entries-${c.contest_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 text-[11px]"><Users className="h-3 w-3" /> Entries</button>
              {!c.winner_name && <button onClick={() => act(c.contest_id, "pick_winner")} data-testid={`eng-contest-pick-${c.contest_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand-amber/50 text-brand-amber px-3 py-1 text-[11px]"><Crown className="h-3 w-3" /> Pick winner</button>}
              <button onClick={async () => { if (await appConfirm("Delete this contest and its entries?")) act(c.contest_id, "delete"); }} data-testid={`eng-contest-delete-${c.contest_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 text-rose-300 px-3 py-1 text-[11px]"><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {entriesFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" data-testid="eng-entries-panel">
          <div className="w-full max-w-md h-full bg-bg-surface border-l border-line overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-sm">{entriesFor.title} — entries</div>
              <button onClick={() => setEntriesFor(null)} data-testid="eng-entries-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
            </div>
            {!entries.length && <div className="text-xs text-ink-muted">No entries yet.</div>}
            {entries.map((e) => (
              <div key={e.entry_id} className="border-b border-line/50 py-2">
                <div className="text-xs font-semibold">{e.name} <span className="text-ink-muted font-normal">· {e.email}</span></div>
                {e.answer && <div className="text-[11px] text-ink-secondary mt-0.5">"{e.answer}"</div>}
                <div className="text-[10px] text-ink-muted mt-0.5">{timeAgo(e.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
